import { Alert, Chip, InputAdornment, Stack, TextField, Theme, Tooltip, Typography } from '@mui/material';
import { debounce, first, flatMap, get, isEmpty, map, size, truncate } from 'lodash';
import { ChangeEvent, useMemo, useState } from 'react';
import { ChevronDown, Search } from 'react-feather';
import { useParams } from 'react-router-dom';
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import IconFeather from 'src/components/common/Icon';
import CustomTable from 'src/components/Table';
import PopoverAction from 'src/components/Table/PopoverAction';
import { getConfidenceTagColor } from 'src/features/AIHSCodeLabeling/config';
import { useNotification } from 'src/hooks/useNotification';
import { CommonResponse } from 'src/model/common';
import { useMutateExportBulkUploadResult } from 'src/services/mutate/useMutateAILabeling';
import { useQueryBulkUploadResult } from 'src/services/queries/useQueryAILabeling';
import { LIMIT_GET_LIST } from 'src/utils/constants/config';
import { ERROR_CODE, SUCCESS_CODE } from 'src/utils/constants/statusCode';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { getNumberOrDefault } from 'src/utils/helpers/number';
import { downloadFile } from 'src/utils/helpers/utilities';

// const dummyData = [
//   {
//     product_name: 'laptop',
//     product_description:
//       'Lenovo laptops are known for their durability, solid performance, and variety of models, including the ThinkPad (business), IdeaPad (personal), Legion (gaming), and Yoga (2-in-1)',
//     hs_code: ['847180', '851762', '847130', '701990', '591190', '961800'],
//     hs_code_description: [
//       'Nuclear Reactors, Boilers, Machinery And Mechanical Appliances; Parts Thereof - Automatic Data Processing Machines And Units Thereof; Magnetic Or Optical Readers, Machines For Transcribing Data Onto Data Media In Coded Form And Machines For Processing Such Data, Not Elsewhere Specified Or Included. - Other Automatic Data Processing Machines - Other Units Of Automatic Data Processing Machines',
//       'Electrical Machinery And Equipment And Parts Thereof; Sound Recorders And Reproducers, Television Image And Sound Recorders And Reproducers, And Parts And Accessories Of Such Articles - Telephone Sets, Including Telephones For Cellular Networks Or For Other Wireless Networks; Other Apparatus For The Transmission Or Reception Of Voice, Images Or Other Data, Including Apparatus For Communication In A Wired Or Wireless Networks (Such As A Local Or Wide Area Network), Other Than Transmission Or Reception Apparatus Of Heading 84.43, 85.25, 85.27 Or 85.28. - Other Apparatus For Transmission Or Reception Of Voices, Images Or Other Data, Including Apparatus For Communication In A Wired Or Wireless Network (Such As A Local Or Wide Area Network) - - Machines For The Reception, Conversion And Transmission Or Regeneration Of Voice, Images Or Other Data, Including Switching And Routing Apparatus',
//       'Nuclear Reactors, Boilers, Machinery And Mechanical Appliances; Parts Thereof - Automatic Data Processing Machines And Units Thereof; Magnetic Or Optical Readers, Machines For Transcribing Data Onto Data Media In Coded Form And Machines For Processing Such Data, Not Elsewhere Specified Or Included. - Other Electronic Calculating Machines - Portable Automatic Data Processing Machines, Weighing Not More Than 10 Kg, Consisting Of At Least A Central Processing Unit, A Keyboard And A Display',
//       'Glass And Glassware - Glass Fibres (Including Glass Wool) And Articles Thereof (For Example, Yarn, Woven Fabrics). - Chemically Bonded Textiles - Other',
//       'Impregnated, Coated, Covered Or Laminated Textile Fabrics; Textile Articles Of A Kind Suitable For Industrial Use - Textile Products And Articles, For Technical Uses, Specified In Note 7 To This Chapter. - Textile Fabrics And Felts, Endless Or Fitted With Linking Devices, Of A Kind Used In Paper-Making Or Similar Machines (For Example, For Pulp Or Asbestos-Cement) - Other',
//       'Miscellaneous Manufactured Articles - Parts Thereof, Other Than Glass Inners - Combs, Hair-Slides And The Like  Tailors’ Dummies And Other Lay Figures; Automata And Other Animated Displays Used For Shop Window Dressing.',
//     ],
//     confidence_level: [98.75, 82.8, 34.86, 22.01, 22.01, 21.78],
//   },
//   {
//     product_name: 'banana',
//     product_description:
//       'Bananas are a sweet, nutritious fruit rich in potassium, vitamin C, and fiber. They are portable, easy to eat, and commonly enjoyed raw, in smoothies, or in baking.',
//     hs_code: ['200799', '293628', '293629', '190219', '071190', '081110'],
//     hs_code_description: [
//       'Preparations Of Vegetables, Fruit, Nuts Or Other Parts Of Plants - Jams, Fruit Jellies, Marmalades, Fruit Or Nut Purée And Fruit Or Nut Pastes, Obtained By Cooking, Whether Or Not Containing Added Sugar Or Other Sweetening Matter. - Other - - Other',
//       'Organic Chemicals - Provitamins And Vitamins, Natural Or Reproduced By Synthesis (Including Natural Concentrates), Derivatives Thereof Used Primarily As Vitamins, And Intermixtures Of The Foregoing, Whether Or Not In Any Solvent. - Vitamins And Their Derivatives, Unmixed - - Vitamin E And Its Derivatives',
//       'Organic Chemicals - Provitamins And Vitamins, Natural Or Reproduced By Synthesis (Including Natural Concentrates), Derivatives Thereof Used Primarily As Vitamins, And Intermixtures Of The Foregoing, Whether Or Not In Any Solvent. - Vitamins And Their Derivatives, Unmixed - - Other Vitamins And Their Derivatives',
//       "Preparations Of Cereals, Flour, Starch Or Milk; Pastrycooks' Products - Pasta, Whether Or Not Cooked Or Stuffed (With Meat Or Other Substances) Or Otherwise Prepared, Such As Spaghetti, Macaroni, Noodles, Lasagne, Gnocchi, Ravioli, Cannelloni; Couscous, Whether Or Not Prepared. - Uncooked Pasta, Not Stuffed Or Otherwise Prepared - - Other",
//       'Edible Vegetables And Certain Roots And Tubers - Vegetables Provisionally Preserved (For Example, By Sulphur Dioxide Gas, In Brine, In Sulphur Water Or In Other Preservative Solutions), But Unsuitable In That State For Immediate Consumption. - Mushrooms And Truffles - Other Vegetables; Mixtures Of Vegetables',
//       'Edible Fruit And Nuts; Peel Of Citrus Fruit Or Melons - Fruit And Nuts, Uncooked Or Cooked By Steaming Or Boiling In Water, Frozen, Whether Or Not Containing Added Sugar Or Other Sweetening Matter. - Cherries - Strawberries',
//     ],
//     confidence_level: [84.27, 77.84, 77.84, 59.33, 32.19, 4.43],
//   },
//   {
//     product_name: 'tuna fish',
//     product_description:
//       'Fish are a diverse group of aquatic animals, known for being a healthy source of protein, omega-3 fatty acids, and essential vitamins. ',
//     hs_code: ['150420', '151610', '051191', '291590', '382319'],
//     hs_code_description: [
//       'Animal Or Vegetable Fats And Oils And Their Cleavage Products; Prepared Edible Fats; Animal Or Vegetable Waxes - Fats And Oils And Their Fractions, Of Fish Or Marine Mammals, Whether Or Not Refined, But Not Chemically Modified. - Mucilages And Thickeners, Whether Or Not Modified, Derived From Vegetable Products - Fats And Oils And Their Fractions, Of Fish, Other Than Liver Oils',
//       'Animal Or Vegetable Fats And Oils And Their Cleavage Products; Prepared Edible Fats; Animal Or Vegetable Waxes - Animal Or Vegetable Fats And Oils And Their Fractions, Partly Or Wholly Hydrogenated, Inter- Esterified, Re-Esterified Or Elaidinised, Whether Or Not Refined, But Not Further Prepared. - Maize (Corn) Oil And Its Fractions - Animal Fats And Oils And Their Fractions',
//       'Products Of Animal Origin, Not Elsewhere Specified Or Included - Animal Products Not Elsewhere Specified Or Included; Dead Animals Of Chapter 1 Or 3, Unfit For Human Consumption. - Other - - Products Of Fish Or Crustaceans, Molluscs Or Other Aquatic Invertebrates; Dead Animals Of Chapter 3',
//       'Organic Chemicals - Saturated Acyclic Monocarboxylic Acids And Their Anhydrides, Halides, Peroxides And Peroxyacids; Their Halogenated, Sulphonated, Nitrated Or Nitrosated Derivatives. - Esters Of Acetic Acid - Other',
//       'Miscellaneous Chemical Products - Industrial Monocarboxylic Fatty Acids; Acid Oils From Refining; Industrial Fatty Alcohols. - Industrial Monocarboxylic Fatty Acids; Acid Oils From Refining  - - Other ',
//     ],
//     confidence_level: [99.33, 66.42, 42.95, 32.23, 32.23],
//   },
//   {
//     product_name: 'leather bag',
//     product_description:
//       'stylish, durable accessories made from animal hide, typically cowhide or goatskin. Known for their high-quality craftsmanship, leather bags are popular for their timeless appeal, with options ranging from handbags to backpacks and briefcases.',
//     hs_code: ['420221', '420211', '420329', '420231', '420291', '420321', '420292'],
//     hs_code_description: [
//       'Articles Of Leather; Saddlery And Harness; Travel Goods, Handbags And Similar Containers; Articles Of Animal Gut (Other Than Silk-Worm Gut) Trunks, Suit-Cases, Vanity-Cases, Executive- Cases, Brief- Cases, School Satchels, Spectacle Cases, Binocular Cases, Camera Cases, Musical Instrument Cases, Gun Cases, Holsters And Similar Containers; Travelling-Bags, Insulated Food Or Beverages Bags, Toilet Bags, Rucksacks, Handbags, Shopping-Bags, Wallets, Purses, Map- Cases, Cigarette-Cases, Tobacco-Pouches, Tool Bags, Sports Bags, Bottle-Cases, Jewellery Boxes, Powder-Boxes, Cutlery Cases And Similar Containers, Of Leather Or Of Composition Leather, Of Sheeting Of Plastics, Of Textile Materials, Of Vulcanised Fibre Or Of Paperboard, Or Wholly Or Mainly Covered With Such Materials Or With Paper. - Handbags, Whether Or Not With Shoulder Strap, Including Those Without Handle - - With Outer Surface Of Leather Or Of Composition Leather',
//       'Articles Of Leather; Saddlery And Harness; Travel Goods, Handbags And Similar Containers; Articles Of Animal Gut (Other Than Silk-Worm Gut) Trunks, Suit-Cases, Vanity-Cases, Executive- Cases, Brief- Cases, School Satchels, Spectacle Cases, Binocular Cases, Camera Cases, Musical Instrument Cases, Gun Cases, Holsters And Similar Containers; Travelling-Bags, Insulated Food Or Beverages Bags, Toilet Bags, Rucksacks, Handbags, Shopping-Bags, Wallets, Purses, Map- Cases, Cigarette-Cases, Tobacco-Pouches, Tool Bags, Sports Bags, Bottle-Cases, Jewellery Boxes, Powder-Boxes, Cutlery Cases And Similar Containers, Of Leather Or Of Composition Leather, Of Sheeting Of Plastics, Of Textile Materials, Of Vulcanised Fibre Or Of Paperboard, Or Wholly Or Mainly Covered With Such Materials Or With Paper. - Trunks, Suit-Cases, Vanity-Cases, Executive-Cases, Brief-Cases, School Satchels And Similar Containers - - With Outer Surface Of Leather Or Of Composition Leather',
//       'Articles Of Leather; Saddlery And Harness; Travel Goods, Handbags And Similar Containers; Articles Of Animal Gut (Other Than Silk-Worm Gut) Articles Of Apparel And Clothing Accessories, Of Leather Or Of Composition Leather. - Gloves, Mittens And Mitts - - Other',
//       'Articles Of Leather; Saddlery And Harness; Travel Goods, Handbags And Similar Containers; Articles Of Animal Gut (Other Than Silk-Worm Gut) Trunks, Suit-Cases, Vanity-Cases, Executive- Cases, Brief- Cases, School Satchels, Spectacle Cases, Binocular Cases, Camera Cases, Musical Instrument Cases, Gun Cases, Holsters And Similar Containers; Travelling-Bags, Insulated Food Or Beverages Bags, Toilet Bags, Rucksacks, Handbags, Shopping-Bags, Wallets, Purses, Map- Cases, Cigarette-Cases, Tobacco-Pouches, Tool Bags, Sports Bags, Bottle-Cases, Jewellery Boxes, Powder-Boxes, Cutlery Cases And Similar Containers, Of Leather Or Of Composition Leather, Of Sheeting Of Plastics, Of Textile Materials, Of Vulcanised Fibre Or Of Paperboard, Or Wholly Or Mainly Covered With Such Materials Or With Paper. - Articles Of A Kind Normally Carried In The Pocket Or In The Handbag - - With Outer Surface Of Leather Or Of Composition Leather',
//       'Articles Of Leather; Saddlery And Harness; Travel Goods, Handbags And Similar Containers; Articles Of Animal Gut (Other Than Silk-Worm Gut) Trunks, Suit-Cases, Vanity-Cases, Executive- Cases, Brief- Cases, School Satchels, Spectacle Cases, Binocular Cases, Camera Cases, Musical Instrument Cases, Gun Cases, Holsters And Similar Containers; Travelling-Bags, Insulated Food Or Beverages Bags, Toilet Bags, Rucksacks, Handbags, Shopping-Bags, Wallets, Purses, Map- Cases, Cigarette-Cases, Tobacco-Pouches, Tool Bags, Sports Bags, Bottle-Cases, Jewellery Boxes, Powder-Boxes, Cutlery Cases And Similar Containers, Of Leather Or Of Composition Leather, Of Sheeting Of Plastics, Of Textile Materials, Of Vulcanised Fibre Or Of Paperboard, Or Wholly Or Mainly Covered With Such Materials Or With Paper. - Other - - With Outer Surface Of Leather Or Of Composition Leather',
//       'Articles Of Leather; Saddlery And Harness; Travel Goods, Handbags And Similar Containers; Articles Of Animal Gut (Other Than Silk-Worm Gut) Articles Of Apparel And Clothing Accessories, Of Leather Or Of Composition Leather. - Gloves, Mittens And Mitts - - Specially Designed For Use In Sports',
//       'Articles Of Leather; Saddlery And Harness; Travel Goods, Handbags And Similar Containers; Articles Of Animal Gut (Other Than Silk-Worm Gut) Trunks, Suit-Cases, Vanity-Cases, Executive- Cases, Brief- Cases, School Satchels, Spectacle Cases, Binocular Cases, Camera Cases, Musical Instrument Cases, Gun Cases, Holsters And Similar Containers; Travelling-Bags, Insulated Food Or Beverages Bags, Toilet Bags, Rucksacks, Handbags, Shopping-Bags, Wallets, Purses, Map- Cases, Cigarette-Cases, Tobacco-Pouches, Tool Bags, Sports Bags, Bottle-Cases, Jewellery Boxes, Powder-Boxes, Cutlery Cases And Similar Containers, Of Leather Or Of Composition Leather, Of Sheeting Of Plastics, Of Textile Materials, Of Vulcanised Fibre Or Of Paperboard, Or Wholly Or Mainly Covered With Such Materials Or With Paper. - Other - - With Outer Surface Of Sheeting Of Plastics Or Of Textile Materials',
//     ],
//     confidence_level: [95.99, 84.99, 79.97, 76.81, 33.95, 28.68, 23.33],
//   },
// ];

const BuildUploadResult = () => {
  const [params, setParams] = useState({
    offset: 0,
    limit: LIMIT_GET_LIST,
    q: '',
  });
  const { id } = useParams();
  const {
    isFetching,
    data: { data, total_records },
  } = useQueryBulkUploadResult({
    id: id,
    ...params,
  });
  const { mutateAsync, isPending } = useMutateExportBulkUploadResult();
  const { notification } = useNotification();

  const dataMapping = map(data, (item) =>
    getArrayDefault(item.hs_code).map((code: string, i: number) => ({
      ...item,
      ...{
        hs_code_single: code,
        hs_code_single_description_single: get(item.hs_code_description, i),
        confidence_level_single: get(item.confidence_level, i),
      },
    })),
  );

  const flatData = flatMap(dataMapping);

  const rowSpan = useMemo(() => {
    if (isEmpty(data)) return 1;
    return size(first(data)?.hs_code) || 1;
  }, [data]);

  const configColumn: Array<ConfigCell<TypeAIFieldMappingResult>> = [
    {
      id: 'product_name',
      label: 'Product Name',
      disableSort: true,
      width: 150,
      styledCell(data, theme: Theme) {
        return {
          borderRight: '1px dashed' + theme.palette.divider,
        };
      },
      rowSpan: (data) => rowSpan,
      cell: (data: TypeAIFieldMappingResult) => (
        <Typography sx={{ wordBreak: 'break-all' }} variant="body2" color={'text.secondary'}>
          {data.product_name}
        </Typography>
      ),
    },
    {
      id: 'product_description',
      label: 'Product Description',
      disableSort: true,
      rowSpan: (data) => rowSpan,
      styledCell(data, theme: Theme) {
        return {
          borderRight: '1px dashed' + theme.palette.divider,
        };
      },
      cell: (data: TypeAIFieldMappingResult) => (
        <Tooltip placement="right-end" title={data.hs_code_description}>
          <Typography sx={{ wordBreak: 'break-all' }} variant="body2" color={'text.secondary'}>
            {truncate(data.product_description, {
              length: 500,
            })}
          </Typography>
        </Tooltip>
      ),
    },
    {
      id: 'hs_code',
      label: 'HS Code',
      disableSort: true,
      align: 'center',
      cell: (data: TypeAIFieldMappingResult) => (
        <Typography variant="body2" color={'text.secondary'}>
          {data.hs_code_single}
        </Typography>
      ),
    },
    {
      id: 'description_hs_code',
      label: 'Description of HS Code',
      disableSort: true,
      cell: (data: TypeAIFieldMappingResult) => (
        <Tooltip title={data.hs_code_single_description_single}>
          <Typography variant="body2" color={'text.secondary'}>
            {truncate(data.hs_code_single_description_single, {
              length: 100,
            })}
          </Typography>
        </Tooltip>
      ),
    },
    {
      id: 'confidence_level',
      label: 'Confidence Level',
      disableSort: true,
      cell: (data: TypeAIFieldMappingResult) => (
        <Chip
          size="small"
          color={getConfidenceTagColor(getNumberOrDefault(data.confidence_level_single))}
          label={`
          Confidence ${data.confidence_level_single} %
          `}
        />
      ),
    },
  ];

  const debounceSearch = debounce(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setParams({ ...params, ...{ page: 0, q: e.target.value } }),
    1000,
  );

  const onExport = async (format_extension: 'csv' | 'xlsx') => {
    const res = await mutateAsync({
      file_id: id,
      format_extension: format_extension,
    });
    downloadFile(res, 'adamftd-export-bulk-upload-file', format_extension);
    const statusCode = res ? SUCCESS_CODE.GET : ERROR_CODE.BAD_REQUEST;
    const responseNoti = new CommonResponse({ status_code: statusCode });
    notification(
      responseNoti,
      {
        title: 'Export Successful', // Success case title
        description: 'Your file has been exported successfully.', // Success case description
      },
      {
        title: 'Export Failed', // Failure case title
        description: 'An error occurred while exporting the file. Please try again.', // Failure case description
      },
    );
  };

  return (
    <Stack spacing={3}>
      <Alert severity="info" color="warning">
        This is a preview version with limited features. For the full experience, please export.
      </Alert>
      <Stack spacing={3} direction={'row'} alignItems={'center'}>
        <Typography color={'primary.main'} variant="h6">
          30{' '}
          <Typography variant="h6" display={'inline'}>
            HS Codes Found
          </Typography>
        </Typography>

        <TextField
          onChange={debounceSearch}
          sx={{ flex: 1 }}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconFeather muiProps={{ fontSize: 'small', color: 'action' }} icon={<Search />} />
              </InputAdornment>
            ),
            style: {
              borderRadius: 1000,
            },
          }}
          placeholder="Search product name"
        />

        <PopoverAction
          listAction={[
            {
              text: 'Export as CSV',
              action: (params?: ExpectedAny) => onExport('csv'),
            },
            {
              text: 'Export as XLSX',
              action: (params?: ExpectedAny) => onExport('xlsx'),
            },
          ]}
        >
          <ButtonWithLoading isLoading={isPending} endIcon={<ChevronDown size={18} />} variant="outlined">
            Export As
          </ButtonWithLoading>
        </PopoverAction>
      </Stack>

      <CustomTable
        onChangePage={(e) => setParams({ ...params, ...{ offset: e } })}
        onChangePageSize={(e) => setParams({ ...params, ...{ offset: 0, limit: e } })}
        page={params.offset}
        pageSize={params.limit}
        totalRecords={total_records}
        loading={isFetching}
        isHover={false}
        dataKey={'product_name'}
        configColumn={configColumn}
        data={flatData}
      />
    </Stack>
  );
};

export default BuildUploadResult;
